import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './loading.css'
import { PwaInstallOfferPanel } from './PwaInstallOfferPanel'
import { ackPwaEntrySession, isPwaEntrySessionAcked } from '../constants/pwaEntryGate'
import {
  enableVercelBlobServiceWorkerCache,
  ensureVercelBlobPreload,
  isLoadDebugEnabled,
  isVercelBlobGateWarmComplete,
  subscribeGatePreloadDetail,
  subscribeVercelBlobPreloadProgress,
  verifyVercelBlobGateWarm,
} from '../utils/vercelAssetPreloadGate'
import type { GatePreloadDetail } from '../utils/vercelAssetPreloadGate'
import { prefetchAssetsForPath } from '../utils/routeAssetPrefetch'
import { useLoadingProgressView } from '../utils/loadingProgressDisplay'
import { GameLoadingScreen } from './GameLoadingScreen'
import type { PwaInstallRunResult } from '../utils/pwaInstallFlow'
import {
  hasInstalledRelatedWebApp,
  isIosDevice,
  isSecureContext,
  isStandaloneDisplay,
  runPwaInstallWarmupOnly,
  showPwaInstallPromptIfAvailable,
} from '../utils/pwaInstallFlow'

type Props = {
  children: React.ReactNode
}

/**
 * `verifying` — the persisted flag says the gate is warm; nothing is shown until the cache audit
 * confirms this page can actually paint those assets (see `verifyVercelBlobGateWarm`). Tens of
 * milliseconds on a healthy cache, and the only way to keep "the flag says so" from turning into
 * a half-drawn land page after storage eviction or a hard reload.
 */
type Phase = 'verifying' | 'gate' | 'blob_loading' | 'installing' | 'finishing' | 'ready'

/** Last-resort cap so a broken manifest cannot block the shell forever (normal loads await full preload). */
const BLOB_PRELOAD_SAFETY_MAX_MS = 45 * 60 * 1000

/** First-run install offer: show on `/` and keep showing through navigation to `/start` until the user continues or installs. */
function isPwaGateEligiblePath(pathname: string): boolean {
  return pathname === '/' || pathname === '/start'
}

/** Where a boot that has to download goes first: the PWA offer on the entry pages, else the bar. */
function coldPhase(pathname: string): Phase {
  if (isStandaloneDisplay()) return 'blob_loading'
  if (!isSecureContext()) return 'blob_loading'
  if (!isPwaEntrySessionAcked() && isPwaGateEligiblePath(pathname)) return 'gate'
  return 'blob_loading'
}

function initialPhase(pathname: string): Phase {
  if (typeof window === 'undefined') return 'blob_loading'
  if (isVercelBlobGateWarmComplete()) return 'verifying'
  return coldPhase(pathname)
}

export const VercelAssetBootstrap: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [phase, setPhase] = useState<Phase>(() => initialPhase(location.pathname))
  const [loaded, setLoaded] = useState(0)
  const [total, setTotal] = useState(0)
  const [gateInstalledRelated, setGateInstalledRelated] = useState<boolean | null>(null)
  const [postInstallIos, setPostInstallIos] = useState(false)
  const [detail, setDetail] = useState<GatePreloadDetail | null>(null)
  /** Read once: the flag cannot change without a reload, and this renders on every progress tick. */
  const [loadDebug] = useState(isLoadDebugEnabled)

  /**
   * A prior visit's warm flag is a claim, not proof. Audit it against the caches this page can
   * really be served from before handing the app over; if it does not hold, fall through to the
   * same cold path a first visit takes (the warm only fetches what is missing).
   */
  useEffect(() => {
    if (phase !== 'verifying') return
    let cancelled = false
    void verifyVercelBlobGateWarm().then((warm) => {
      if (cancelled) return
      setPhase(warm ? 'ready' : coldPhase(window.location.pathname))
    })
    return () => {
      cancelled = true
    }
  }, [phase])

  useEffect(() => {
    if (phase !== 'gate') return
    if (isPwaGateEligiblePath(location.pathname)) return
    ackPwaEntrySession()
    setPhase('blob_loading')
  }, [location.pathname, phase])

  useEffect(() => {
    if (phase !== 'gate') return
    let cancelled = false
    void hasInstalledRelatedWebApp().then((v) => {
      if (!cancelled) setGateInstalledRelated(v)
    })
    return () => {
      cancelled = true
    }
  }, [phase])

  /**
   * The bar is driven through this rather than straight off `loaded / total`: the warm reports
   * nothing at all while it waits for the service worker and scans Cache Storage, and can finish
   * short of the count (assets the CDN reports as gone, a partial-complete pass). Without it the
   * screen regularly vanished at 0% on an already-cached start. `phase` resets the floor so the
   * install phase does not inherit the blob phase's fill.
   */
  const { pct, visible: loadingVisible } = useLoadingProgressView(
    loaded,
    total,
    phase === 'finishing',
    phase === 'finishing' ? 'blob_loading' : phase,
  )

  // 100% has been on screen for its beat — hand the app over.
  useEffect(() => {
    if (phase === 'finishing' && !loadingVisible) setPhase('ready')
  }, [phase, loadingVisible])

  const runBlobPreload = useCallback(() => {
    if (isVercelBlobGateWarmComplete()) {
      setPhase('ready')
      return
    }
    setLoaded(0)
    setTotal(0)
    const unsub = subscribeVercelBlobPreloadProgress((l, t) => {
      setLoaded(l)
      setTotal(t)
    })
    // Already coalesced to ~8 updates/second inside the gate, so this is safe to hand to state.
    const unsubDetail = subscribeGatePreloadDetail(setDetail)
    const safety = new Promise<void>((resolve) => {
      window.setTimeout(resolve, BLOB_PRELOAD_SAFETY_MAX_MS)
    })
    const preload = ensureVercelBlobPreload()
    void preload.catch(() => {
      /* errors already logged inside blob warm; shell still proceeds */
    })
    void Promise.race([preload, safety]).finally(() => {
      unsub()
      unsubDetail()
      // Not 'ready' yet: the bar has to be seen reaching 100% first (see `useLoadingProgressView`).
      setPhase('finishing')
    })
  }, [])

  useEffect(() => {
    if (phase !== 'blob_loading') return
    if (isVercelBlobGateWarmComplete()) {
      setPhase('ready')
      return
    }
    runBlobPreload()
  }, [phase, runBlobPreload])

  useEffect(() => {
    if (phase !== 'installing') return
    setPostInstallIos(false)
    setLoaded(0)
    setTotal(0)
    let cancelled = false
    void runPwaInstallWarmupOnly({
      onProgress: (l: number, t: number) => {
        if (!cancelled) {
          setLoaded(l)
          setTotal(t)
        }
      },
    })
      .then((result: PwaInstallRunResult) => {
        if (cancelled) return
        ackPwaEntrySession()
        if (result === 'standalone' || result === 'insecure') {
          setPhase('ready')
          return
        }
        if (result === 'ios-instructions') {
          setPostInstallIos(true)
          return
        }
        setPhase('ready')
      })
      .catch(() => {
        if (cancelled) return
        ackPwaEntrySession()
        setPhase('ready')
      })
    return () => {
      cancelled = true
    }
  }, [phase])

  const onContinueBrowser = useCallback(() => {
    ackPwaEntrySession()
    setPhase(isVercelBlobGateWarmComplete() ? 'ready' : 'blob_loading')
  }, [])

  const onInstallApp = useCallback(() => {
    showPwaInstallPromptIfAvailable()
    setPhase('installing')
  }, [])

  const onIosGotIt = useCallback(() => {
    setPostInstallIos(false)
    setPhase('ready')
  }, [])

  /**
   * Pull the current destination's artwork ahead of the general background sweep. Without this the
   * sweep works in manifest order, so a page opened early waits on a cold fetch even though the app
   * has been downloading continuously.
   */
  useEffect(() => {
    if (phase !== 'ready') return
    prefetchAssetsForPath(location.pathname)
  }, [phase, location.pathname])

  useEffect(() => {
    if (phase !== 'ready') return
    // Idempotent; `ensureVercelBlobPreload` already enables this up front so the preload itself
    // is cached. Kept for the path where the gate was verified warm and skipped.
    enableVercelBlobServiceWorkerCache()
  }, [phase])

  const showInstallButton = !isStandaloneDisplay() && isSecureContext()

  // Nothing at all while the audit runs: mounting the app here would start the very on-demand
  // fetches the gate exists to prevent, and a loading screen for a check that usually takes tens
  // of milliseconds would flash on every healthy boot.
  if (phase === 'verifying') return null

  if (phase === 'blob_loading' && isVercelBlobGateWarmComplete()) {
    return <>{children}</>
  }

  if (phase === 'gate') {
    const installedHint = gateInstalledRelated === true
    return (
      <>
        {/* Mount `/` (and full app shell) underneath so the route is visible through the overlay */}
        {children}
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
            padding:
              'max(48px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
            background: 'rgba(6, 8, 14, 0.38)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            pointerEvents: 'auto',
          }}
        >
        <PwaInstallOfferPanel
          titleId="pwa-gate-title"
          installedHint={installedHint}
          showInstallButton={showInstallButton}
          onInstallApp={onInstallApp}
          onContinueBrowser={onContinueBrowser}
        />
        </div>
      </>
    )
  }

  if (phase === 'blob_loading' || phase === 'installing' || phase === 'finishing') {
    return (
      <>
        {phase === 'installing' ? null : children}
        <GameLoadingScreen
          pct={pct}
          total={total}
          detail={detail}
          debug={loadDebug}
          labelOverride={phase === 'installing' ? t('pwa.installPreparing') : undefined}
        />
        <div className="game-loading-overlay-extras">
          {phase === 'installing' && postInstallIos && isIosDevice() && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '8%',
                transform: 'translateX(-50%)',
                width: 'min(400px, 92vw)',
                padding: 16,
                borderRadius: 12,
                background: 'rgba(15,18,28,0.92)',
                border: '1px solid rgba(255,200,120,0.35)',
                color: 'rgba(255,235,220,0.95)',
                fontSize: 14,
                zIndex: 20,
                textAlign: 'left',
              }}
            >
              <p style={{ margin: '0 0 12px', fontWeight: 600 }}>{t('pwa.iosInstructionsTitle')}</p>
              <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                <li>{t('pwa.iosStepShare')}</li>
                <li>{t('pwa.iosStepAdd')}</li>
                <li>{t('pwa.iosStepOpen')}</li>
              </ol>
              <button
                type="button"
                onClick={onIosGotIt}
                style={{
                  marginTop: 14,
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(180deg, #d94 0%, #a62 100%)',
                  color: '#1a1008',
                  fontWeight: 700,
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                {t('pwa.iosGotIt')}
              </button>
            </div>
          )}
        </div>
      </>
    )
  }

  return <>{children}</>
}
